import CloseIcon from "@mui/icons-material/Close";
import {
	Box,
	ButtonOwnProps,
	Dialog,
	DialogContent,
	dialogContentClasses,
	DialogContentProps,
	DialogProps,
	DialogTitle,
	dialogTitleClasses,
	DialogTitleProps,
	IconButton,
	styled
} from "@mui/material";

interface IAGDialogTitleProps extends DialogTitleProps {
	color?: ButtonOwnProps["color"];
	onClose?: (event: {}, reason: "backdropClick" | "escapeKeyDown") => void;
}

interface IAGDialogContentProps extends DialogContentProps {
	showCloseButton?: boolean;
	bgGrey?: boolean;
	onClose?: (event: {}, reason: "backdropClick" | "escapeKeyDown") => void;
}

interface IAGDialogProps extends DialogProps {
	title?: string;
	color?: ButtonOwnProps["color"];
	bgGrey?: boolean;
}

const AGDialogTitle = styled(({ onClose, children, ...props }: IAGDialogTitleProps) => (
	<DialogTitle {...props}>
		{children}

		<Box
			sx={{
				position: "absolute",
				top: 0,
				right: "0.5rem",
				bottom: 0,
				display: "flex",
				alignItems: "center",
				justifyContent: "center"
			}}
		>
			{onClose ? (
				<IconButton onClick={() => onClose({}, "backdropClick")} color="inherit">
					<CloseIcon />
				</IconButton>
			) : null}
		</Box>
	</DialogTitle>
))(({ color, theme }) => {
	const getBackgroundColor = (): string => {
		switch (color) {
			case "primary":
				return theme.palette.primary.main;

			case "success":
				return theme.palette.success.main;

			case "error":
				return theme.palette.error.main;

			case "warning":
				return theme.palette.warning.main;

			case "info":
				return theme.palette.info.main;

			default:
				return "white";
		}
	};

	const getTextColor = (): string => {
		switch (color) {
			case "primary":
			case "success":
			case "error":
			case "warning":
			case "info":
				return "white";

			default:
				return "#3D3D3D";
		}
	};

	return {
		[`&.${dialogTitleClasses.root}`]: {
			position: "relative",
			textAlign: "center",
			backgroundColor: getBackgroundColor(),
			color: getTextColor()
		}
	};
});

const AGDialogContent = styled(({ showCloseButton, children, onClose, ...props }: IAGDialogContentProps) => (
	<DialogContent {...props}>
		{children}
		{showCloseButton && onClose ? (
			<Box sx={{ position: "absolute", top: 0, right: 0 }}>
				<IconButton onClick={onClose ? () => onClose({}, "backdropClick") : undefined} color="inherit">
					<CloseIcon />
				</IconButton>
			</Box>
		) : null}
	</DialogContent>
))(({ bgGrey }) => ({
	[`&.${dialogContentClasses.root}`]: {
		paddingTop: "1rem",
		position: "relative",
		backgroundColor: bgGrey ? "#f8f8f8" : "white"
	}
}));

export const AGDialog = styled(({ title, color, children, bgGrey, ...props }: IAGDialogProps) => (
	<Dialog fullWidth maxWidth="md" {...props}>
		{title ? (
			<AGDialogTitle color={color} onClose={props.onClose}>
				{title}
			</AGDialogTitle>
		) : null}

		<AGDialogContent showCloseButton={!title} bgGrey={bgGrey} onClose={props.onClose}>
			{children}
		</AGDialogContent>
	</Dialog>
))(() => ({}));
